// Сила замка и штурмы: армии игрока и альянса собираются в замке организатора и идут на цель вместе.
import type { Balance } from './balance.js';
import { armyPower } from './battle.js';

/** Сколько отрядов (замков-участников, включая организатора) помещается в один штурм. */
export const RALLY_SLOTS = 25;
/** Сколько минут длится сбор штурма (выбирает организатор). */
export const RALLY_WAIT_MINUTES = [5, 10, 30, 60] as const;

/**
 * Сила замка: уровень, развитие (сумма уровней зданий), его войска (гарнизон и отряды в походах)
 * и — у главного замка — герои игрока. Сила игрока в рейтинге = сумма сил всех его замков.
 */
export function castlePower(
  balance: Balance,
  c: { level: number; buildingLevels: number; units: Record<string, number>; heroLevels?: number },
): number {
  return (
    c.level * 1000 +
    c.buildingLevels * 25 +
    Math.round(armyPower(balance, c.units) / 10) +
    (c.heroLevels ?? 0) * 100
  );
}

/**
 * Делит выживших штурма между отрядами-участниками: каждый тип воинов — пропорционально тому,
 * сколько таких воинов привёл отряд (метод наибольшего остатка, сумма сохраняется точно).
 */
export function splitSurvivors(
  contributions: Record<string, number>[],
  survivors: Record<string, number>,
): Record<string, number>[] {
  const out = contributions.map(() => ({}) as Record<string, number>);
  for (const [unit, alive] of Object.entries(survivors)) {
    if (alive <= 0) continue;
    const sent = contributions.map((c) => Math.max(0, c[unit] ?? 0));
    const shares = largestRemainder(sent, alive);
    shares.forEach((n, i) => {
      if (n > 0) out[i]![unit] = n;
    });
  }
  return out;
}

/** Делит добычу между отрядами пропорционально весам (грузоподъёмности выживших). */
export function splitLoot(weights: number[], loot: Record<string, number>): Record<string, number>[] {
  const out = weights.map(() => ({}) as Record<string, number>);
  for (const [res, amount] of Object.entries(loot)) {
    if (amount <= 0) continue;
    const shares = largestRemainder(weights, Math.floor(amount));
    shares.forEach((n, i) => {
      if (n > 0) out[i]![res] = n;
    });
  }
  return out;
}

/** Целочисленное деление total пропорционально весам; сумма долей ровно total (если веса не нулевые). */
function largestRemainder(weights: number[], total: number): number[] {
  const sum = weights.reduce((a, w) => a + Math.max(0, w), 0);
  if (sum <= 0 || total <= 0) return weights.map(() => 0);
  const exact = weights.map((w) => (Math.max(0, w) * total) / sum);
  const res = exact.map(Math.floor);
  let left = total - res.reduce((a, n) => a + n, 0);
  const order = exact.map((e, i) => ({ i, r: e - Math.floor(e) })).sort((a, b) => b.r - a.r || a.i - b.i);
  for (const { i } of order) {
    if (left <= 0) break;
    if (weights[i]! <= 0) continue;
    res[i]!++;
    left--;
  }
  return res;
}
